import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiAward, FiPlus, FiSearch, FiTrash2, FiX, FiMail, FiArrowLeft, FiUser, FiCheck, FiAlertCircle } = FiIcons;

const UtrederDirectory = () => {
  const navigate = useNavigate();
  const { utredere, addUtreder, deleteUtreder, updateUtreder } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({ full_name: '', email: '', company_name: '', title_role: 'Utreder' });
  
  // Midlertidig diagnostikk-state
  const [diag, setDiag] = useState({});

  const filtered = (utredere || []).filter(u => {
    const name = u.full_name || u.fullName || '';
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleManualAdd = (e) => {
    e.preventDefault();
    addUtreder({ ...formData, active: true, status: 'approved' });
    setShowAddModal(false);
    setFormData({ full_name: '', email: '', company_name: '', title_role: 'Utreder' });
  };

  const handleApprove = async (id) => {
    // Denne kalles nå ETTER at klikk-diagnostikken er satt i selve knappen
    if (window.confirm("Vil du godkjenne denne utreder-søknaden?")) {
      const result = await updateUtreder(id, { 
        active: true, 
        status: 'approved' 
      });
      
      setDiag(prev => ({
        ...prev,
        [id]: { ...prev[id], result: JSON.stringify(result) }
      }));
    }
  };

  const handleReject = async (id) => {
    if (window.confirm("Vil du slette/avvise denne utrederen permanent?")) {
      await deleteUtreder(id);
    }
  };

  const getStatusDisplay = (u) => {
    if (u.status === 'approved' || u.active === true) {
      return <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider">Godkjent</span>;
    }
    
    if (u.status === 'pending_review' || u.status === 'pending_approval' || !u.active) {
      return <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider">Til godkjenning</span>;
    }
    
    return <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider">{u.status || 'Ukjent'}</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <header className="bg-white border-b p-4 flex justify-between items-center sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-4">
          <button type="button" onClick={() => navigate('/superadmin')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <SafeIcon icon={FiArrowLeft} />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Utreder-register</h1>
        </div>
        <div className="flex gap-3">
          <div className="relative w-64">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-3 text-gray-400" />
            <input 
              type="text" 
              placeholder="Søk navn..." 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
              className="w-full pl-10 pr-4 py-2 border rounded-xl bg-gray-50 outline-none focus:ring-2 focus:ring-indigo-500 transition-all" 
            />
          </div>
          <button type="button" onClick={() => setShowAddModal(true)} className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-indigo-700 shadow-sm transition-colors">
            <SafeIcon icon={FiPlus} /> Legg til manuelt
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-[10px] font-black uppercase text-gray-400 border-b">
              <tr>
                <th className="px-6 py-4 tracking-widest">Navn / Diagnostikk</th>
                <th className="px-6 py-4 tracking-widest">Firma</th>
                <th className="px-6 py-4 tracking-widest">Status</th>
                <th className="px-6 py-4 text-right tracking-widest">Handling</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(u => {
                const isPending = !(u.status === 'approved' || u.active === true);
                const d = diag[u.id] || {};
                return (
                  <tr key={u.id} className="hover:bg-indigo-50/20 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-bold text-gray-900">{u.full_name || u.fullName}</div>
                      <div className="text-xs text-gray-500 mb-2">{u.email}</div>
                      
                      {/* MIDLERTIDIG DIAGNOSTIKK FELT */}
                      <div className="mt-2 p-3 bg-slate-900 text-[10px] text-green-400 font-mono rounded-lg border border-slate-700 shadow-inner max-w-xs relative z-0">
                        <p className="font-black text-white border-b border-slate-700 mb-1 pb-1 uppercase tracking-tighter">Runtime Diagnostikk</p>
                        <p><span className="text-slate-500">ID:</span> {u.id}</p>
                        <p><span className="text-slate-500">STATUS:</span> {u.status}</p>
                        <p><span className="text-slate-500">ACTIVE:</span> {String(u.active)}</p>
                        <p><span className="text-slate-500">RENDER:</span> {new Date().toLocaleTimeString()}</p>
                        <p className="mt-1 pt-1 border-t border-slate-800">
                          <span className="text-slate-500">KLIKK:</span> <span className={d.clicked === 'JA' ? 'text-green-400 font-black' : 'text-red-500'}>{d.clicked || 'NEI'}</span>
                        </p>
                        <p className="text-yellow-400"><span className="text-slate-500">RESULTAT:</span> {d.result || '-'}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-medium align-top pt-6">
                      {u.company_name || u.companyName || 'Ikke oppgitt'}
                    </td>
                    <td className="px-6 py-4 align-top pt-6">
                      {getStatusDisplay(u)}
                    </td>
                    <td className="px-6 py-4 text-right align-top pt-6">
                      <div className="flex justify-end gap-2 relative z-50">
                        {isPending && (
                          <button 
                            type="button"
                            style={{ pointerEvents: 'auto', position: 'relative', zIndex: 60 }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              // Sett JA umiddelbart synkront
                              setDiag(prev => ({
                                ...prev,
                                [u.id]: { ...prev[u.id], clicked: 'JA', result: 'Klikk registrert!' }
                              }));
                              // Kall så den asynkrone logikken
                              handleApprove(u.id);
                            }} 
                            className="bg-green-600 text-white px-4 py-3 rounded-lg font-black text-[10px] uppercase tracking-tighter shadow-lg hover:bg-green-700 transition-all active:scale-95"
                          >
                            [ GODKJENN TEST ]
                          </button>
                        )}
                        <button 
                          type="button"
                          onClick={() => handleReject(u.id)} 
                          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        >
                          <SafeIcon icon={FiTrash2} className="text-lg" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-6 py-20 text-center text-gray-400 italic">
                    Ingen utredere eller søknader funnet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AnimatePresence>
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-white p-8 rounded-3xl max-w-md w-full shadow-2xl">
              <h3 className="text-xl font-bold mb-6 text-indigo-900">Legg til Utreder</h3>
              <form onSubmit={handleManualAdd} className="space-y-4">
                <input type="text" placeholder="Fullt Navn" required className="w-full p-3 border rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" value={formData.full_name} onChange={e => setFormData({ ...formData, full_name: e.target.value })} />
                <input type="email" placeholder="E-post" required className="w-full p-3 border rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
                <input type="text" placeholder="Firma / Selskap" className="w-full p-3 border rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" value={formData.company_name} onChange={e => setFormData({ ...formData, company_name: e.target.value })} />
                <div className="pt-4 flex flex-col gap-2">
                  <button type="submit" className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold shadow-lg hover:bg-indigo-700 transition-all">Lagre</button>
                  <button type="button" onClick={() => setShowAddModal(false)} className="w-full text-gray-500 font-bold py-2 hover:text-gray-700">Avbryt</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UtrederDirectory;